/**
 * Observability API. Added for the FE↔BE integration (Round 4).
 *
 * Backs the Logs modal with real data:
 *   GET /observability/:id/executions/stats — pass/fail counts + success rate
 *   GET /observability/:id/executions       — per-run execution log rows
 *   GET /observability/:id/audit            — append-only config audit events
 *
 * All three are Softknack-internal routes, so we send internal=true.
 *
 * NOTE on executions: a freshly-seeded connector has zero real runs, and that is
 * shown as an empty state. The modal used to fall back to synthesized rows here
 * "so the demo view is never blank" — fabricated traffic rendered identically to
 * real traffic, which is indefensible in an ops surface. Everything this module
 * returns is real backend data.
 */
import { apiFetch } from './client';

export interface BeStats {
  total: number;
  last_24h: number;
  success_24h: number;
  error_24h: number;
  success_rate_24h: number; // 0..1
}

export interface BeExecution {
  execution_id?: string;
  status: string; // ok | error
  operation_id: string | null;
  error_category: string | null; // retryable | conflict | not_found | unauthorized | permanent
  latency_ms: number | null;
  detail: string | null;
  executed_at: string;
}

export interface BeAuditEvent {
  event_type: string; // approval | detail_edit | mcp_update | reauth | credential_save | secret_rotation | version_add
  actor: string;
  occurred_at: string;
  detail: unknown;
}

export interface ObservabilityData {
  stats: BeStats;
  executions: BeExecution[];
  audit: BeAuditEvent[];
}

// ---- BE → Logs-modal shape adapters ----

/** A relative "Xm/Xh ago" string from an ISO timestamp and a fixed "now". */
function ago(iso: string, nowMs: number): string {
  const diffMin = Math.max(0, Math.round((nowMs - Date.parse(iso)) / 60000));
  if (diffMin < 60) return diffMin + 'm ago';
  return Math.floor(diffMin / 60) + 'h ' + (diffMin % 60) + 'm ago';
}

/** Map BE execution rows to the Logs modal's ExecRow-ish shape. */
export function toExecRows(execs: BeExecution[], nowMs: number): Array<{
  id: number; op: string; ago: string; status: string; statusLabel: string;
  statusTone: string; latency: string; detail: string;
}> {
  return execs.map((e, i) => ({
    id: i,
    op: e.operation_id || (e.status === 'ok' ? 'execution' : 'execution · failed'),
    ago: ago(e.executed_at, nowMs),
    status: e.status,
    statusLabel: e.status,
    statusTone: e.status === 'ok' ? 'success' : 'danger',
    latency: e.latency_ms != null ? e.latency_ms + 'ms' : '—',
    detail: e.detail || e.error_category || (e.status === 'ok' ? 'committed' : 'error'),
  }));
}

/** Map BE audit events to the Logs modal's audit-row shape. */
export function toAuditRows(events: BeAuditEvent[]): Array<{
  msg: string; time: string; actor: string; kind: string; tone: string;
}> {
  return events.map((e) => {
    // Tone mirrors the FE's existing convention: publish/approval=accent,
    // auth-ish events=warn, everything else=info.
    const tone =
      e.event_type === 'approval' || e.event_type === 'version_add'
        ? 'accent'
        : e.event_type === 'reauth' || e.event_type === 'credential_save' || e.event_type === 'secret_rotation'
        ? 'warn'
        : 'info';
    return {
      msg: humanizeEvent(e.event_type),
      time: e.occurred_at,
      actor: e.actor,
      kind: e.event_type,
      tone,
    };
  });
}

// Human-readable label for a BE audit event_type (closed set from the schema).
function humanizeEvent(type: string): string {
  switch (type) {
    case 'approval': return 'Mapping approved & published';
    case 'detail_edit': return 'Connector details edited';
    case 'mcp_update': return 'MCP connection updated';
    case 'reauth': return 'Connection re-authorized';
    case 'credential_save': return 'Tenant connected (credentials saved by reference)';
    case 'secret_rotation': return 'Webhook signing secret rotated';
    case 'version_add': return 'New adapter version added';
    default: return type;
  }
}

/** Load all three observability views for an integration in one call. */
export async function loadObservability(integrationId: string): Promise<ObservabilityData> {
  const [stats, execs, audit] = await Promise.all([
    apiFetch<BeStats>(`/observability/${integrationId}/executions/stats`, { internal: true }),
    apiFetch<{ executions: BeExecution[] }>(`/observability/${integrationId}/executions`, { internal: true }),
    apiFetch<{ audit: BeAuditEvent[] }>(`/observability/${integrationId}/audit`, { internal: true }),
  ]);
  return { stats, executions: execs.executions, audit: audit.audit };
}
